import { type FC, useState } from "react";
import type { MyBusinessLocationMediaItem } from "shared";
import { useEventCallback } from "usehooks-ts";
import { useModal } from "../common/modal/Modal";
import { ButtonIcon } from "../components/ButtonIcon";
import { ModalConfirmation } from "../settings/ModalConfirmation";
import { useGoogleMyBusinessActions } from "../settings/useIntegrations";
import "./BusinessMedia.css";

type Props = {
	locationID: string;
	mediaItem: MyBusinessLocationMediaItem;
};

export const BusinessMedia: FC<Readonly<Props>> = ({
	mediaItem,
	locationID,
}) => {
	const { open, close } = useModal();
	const { deleteBusinessMedia } = useGoogleMyBusinessActions();
	const { mutateAsync: deleteMutateAsync } = deleteBusinessMedia;
	const [src, setSrc] = useState(
		() => mediaItem.thumbnailUrl?.trim() || mediaItem.url,
	);

	const onDeleteConfirm = useEventCallback(async (mediaID: string) => {
		await deleteMutateAsync({
			locationID,
			mediaID,
		});
		close();
	});

	const handleDeleteMedia = useEventCallback(() => {
		open(ModalConfirmation, {
			prompt: "Are you sure you want to delete this media?",
			onConfirm: () => onDeleteConfirm(mediaItem.ID),
		});
	});

	const handleImageError = useEventCallback(() => {
		const full = mediaItem.url?.trim();
		if (full && src !== full) {
			setSrc(full);
		}
	});

	const showImage =
		mediaItem.type === "PHOTO" ||
		mediaItem.type === "VIDEO" ||
		mediaItem.type === "UNKNOWN";

	return (
		<figure className="business-media-item">
			<ButtonIcon
				className="delete"
				icon="delete"
				onClick={handleDeleteMedia}
			/>
			<a href={mediaItem.url} target="_blank" rel="noreferrer">
				{showImage && src ? (
					<img
						src={src}
						alt={mediaItem.description || "Business media"}
						referrerPolicy="no-referrer"
						onError={handleImageError}
					/>
				) : null}
			</a>
		</figure>
	);
};
